import {
  BookOpen,
  Brain,
  Bug,
  Check,
  Copy,
  Clock3,
  Database,
  Download,
  Ellipsis,
  ExternalLink,
  Home,
  LogOut,
  MessageSquarePlus,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  Search,
  Send,
  Settings,
  Sparkles,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MDRenderer } from "../components/MDRenderer";
import { PinboardWorkspace } from "../components/PinboardWorkspace";
import { QueryResultChart } from "../components/QueryResultChart";
import { QueryResultTable } from "../components/QueryResultTable";
import { QuerygenDebugDialog } from "../components/QuerygenDebugDialog";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiRequest, querygenApi } from "../lib/api";
import {
  addPanelToPinboard,
  createPinboard,
  readPanelResults,
  readPinboards,
  removePanelResult,
  removePanelFromPinboard,
  writePanelResult,
  writePinboards,
  type Pinboard,
  type PinboardPanelKind,
  type PinboardPanelResult,
} from "../lib/pinboards";
import type {
  AgentRecord,
  QuerygenChartConfig,
  QueryMessage,
  QuerygenAssistantMetadata,
  QuerygenDataArtifact,
  QuerygenDebugInformation,
  QuerygenLegacyChartPayload,
  QuerygenLegacyTablePayload,
  QuerygenUnavailablePayload,
  QuerygenConversationSummary,
} from "../types";
import { useAuth } from "../hooks/useAuth";
import { APP_ROUTES } from "../routes";
import "./querygen.css";

const makeUserName = (email: string | undefined): string => {
  if (!email) {
    return "User";
  }

  const [name] = email.split("@");
  return name.replace(/[._-]+/g, " ");
};

const isAssistantMetadata = (
  value: QueryMessage["metadata"],
): value is QuerygenAssistantMetadata =>
  Boolean(value) && typeof value === "object";

const parseJsonValue = (value: string): unknown => {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
};

const isLegacyTablePayload = (value: unknown): value is QuerygenLegacyTablePayload =>
  Boolean(value) &&
  typeof value === "object" &&
  (value as QuerygenLegacyTablePayload).type === "table" &&
  Array.isArray((value as QuerygenLegacyTablePayload).headers) &&
  Array.isArray((value as QuerygenLegacyTablePayload).rows);

const isLegacyChartPayload = (value: unknown): value is QuerygenLegacyChartPayload =>
  Boolean(value) &&
  typeof value === "object" &&
  Array.isArray((value as QuerygenLegacyChartPayload).data);

const isUnavailablePayload = (value: unknown): value is QuerygenUnavailablePayload =>
  Boolean(value) &&
  typeof value === "object" &&
  (value as QuerygenUnavailablePayload).type === "unavailable" &&
  typeof (value as QuerygenUnavailablePayload).reason === "string";

const PIPE_ROW_PATTERN = /^\s*[^|\n]+(?:\|[^|\n]+){2,}\s*$/;

const stripInlineResultTable = (
  text: string,
  metadata: QueryMessage["metadata"],
): string => {
  if (!isAssistantMetadata(metadata) || !metadata.dataArtifact?.rows?.length) {
    return text.trim();
  }

  const lines = text.split("\n");
  let bestStart = -1;
  let bestEnd = -1;
  let currentStart = -1;

  for (let index = 0; index <= lines.length; index += 1) {
    const line = lines[index] ?? "";
    const isPipeRow = PIPE_ROW_PATTERN.test(line.trim());

    if (isPipeRow && currentStart === -1) {
      currentStart = index;
      continue;
    }

    if (!isPipeRow && currentStart !== -1) {
      if (index - currentStart >= 2 && index - currentStart > bestEnd - bestStart) {
        bestStart = currentStart;
        bestEnd = index;
      }
      currentStart = -1;
    }
  }

  if (bestStart === -1 || bestEnd === -1) {
    return text.trim();
  }

  const cleanedLines = lines.filter((_, index) => index < bestStart || index >= bestEnd);
  return cleanedLines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
};

const toLegacyTablePayloadFromMetadata = (
  metadata: QueryMessage["metadata"],
): QuerygenLegacyTablePayload | null => {
  const dataArtifacts = getDataArtifactsFromMetadata(metadata);
  if (!dataArtifacts.length) {
    return null;
  }

  const artifact = dataArtifacts[dataArtifacts.length - 1];
  if (artifact.unavailableReason) {
    return null;
  }
  const headers = Object.keys(artifact.rows[0] ?? {});
  return {
    type: "table",
    headers,
    rows: artifact.rows.map((row) => headers.map((header) => row[header] ?? null)),
    pagination: null,
  };
};

const getDataArtifactsFromMetadata = (
  metadata: QueryMessage["metadata"],
): QuerygenDataArtifact[] => {
  if (!isAssistantMetadata(metadata)) {
    return [];
  }

  if (Array.isArray(metadata.dataArtifacts)) {
    return metadata.dataArtifacts.filter(
      (artifact): artifact is QuerygenDataArtifact =>
        Boolean(artifact) && typeof artifact === "object" && !Array.isArray(artifact),
    );
  }

  if (
    metadata.dataArtifact &&
    typeof metadata.dataArtifact === "object" &&
    !Array.isArray(metadata.dataArtifact)
  ) {
    return [metadata.dataArtifact];
  }

  return [];
};

const toLegacyTablePayloadFromArtifact = (
  artifact: QuerygenDataArtifact,
): QuerygenLegacyTablePayload | null => {
  if (artifact.unavailableReason) {
    return null;
  }

  const headers = Object.keys(artifact.rows[0] ?? {});
  return {
    type: "table",
    headers,
    rows: artifact.rows.map((row) => headers.map((header) => row[header] ?? null)),
    pagination: null,
  };
};

const toLegacyChartPayloadFromArtifact = (
  artifact: QuerygenDataArtifact,
): QuerygenLegacyChartPayload | null =>
  artifact.rows.length
    ? {
        data: artifact.rows,
        pagination: null,
      }
    : null;

const extractDebugInformation = (
  debugInformation: string | undefined,
  metadata: QueryMessage["metadata"],
): QuerygenDebugInformation | null => {
  if (debugInformation) {
    const parsed = parseJsonValue(debugInformation);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as QuerygenDebugInformation;
    }
  }

  if (!isAssistantMetadata(metadata)) {
    return null;
  }

  if (
    metadata.debug_information &&
    typeof metadata.debug_information === "object" &&
    !Array.isArray(metadata.debug_information)
  ) {
    return metadata.debug_information as QuerygenDebugInformation;
  }

  if (!metadata.dataArtifact) {
    return null;
  }

  return {
    query: metadata.dataArtifact.query,
    chartConfig: metadata.dataArtifact.chartConfig ?? null,
    analysis: metadata.debug?.llmOutput ?? "",
  };
};

const getTablePayload = (message: QueryMessage): QuerygenLegacyTablePayload | null => {
  const parsed = parseJsonValue(message.text);
  if (isLegacyTablePayload(parsed)) {
    return parsed;
  }

  return toLegacyTablePayloadFromMetadata(message.metadata);
};

const getChartPayload = (
  message: QueryMessage,
  debugInformation: QuerygenDebugInformation | null,
): QuerygenLegacyChartPayload | null => {
  const dataArtifacts = getDataArtifactsFromMetadata(message.metadata);
  if (dataArtifacts.length === 1) {
    return toLegacyChartPayloadFromArtifact(dataArtifacts[0]);
  }

  if (!debugInformation?.chartConfig) {
    return null;
  }

  const parsed = parseJsonValue(message.text);
  if (isLegacyChartPayload(parsed)) {
    return parsed;
  }

  if (
    isAssistantMetadata(message.metadata) &&
    getDataArtifactsFromMetadata(message.metadata)[0]?.rows?.length
  ) {
    return {
      data: getDataArtifactsFromMetadata(message.metadata)[0].rows,
      pagination: null,
    };
  }

  return null;
};

const getUnavailableReason = (message: QueryMessage): string | null => {
  const dataArtifacts = getDataArtifactsFromMetadata(message.metadata);
  if (dataArtifacts.length === 1 && dataArtifacts[0]?.unavailableReason) {
    return dataArtifacts[0].unavailableReason;
  }

  const parsed = parseJsonValue(message.text);
  return isUnavailablePayload(parsed) ? parsed.reason : null;
};

const tablePayloadToObjects = (
  payload: QuerygenLegacyTablePayload,
): Array<Record<string, unknown>> =>
  payload.rows.map((row) =>
    payload.headers.reduce<Record<string, unknown>>((record, header, index) => {
      record[header] = row[index] ?? null;
      return record;
    }, {}),
  );

const makePinboardPanelTitle = (
  kind: PinboardPanelKind,
  chartTitle: string | undefined,
  timestamp: number,
  resultIndex: number,
): string => {
  if (chartTitle?.trim()) {
    return chartTitle.trim();
  }

  const label = kind === "chart" ? "Chart" : "Table";
  const time = new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return `${label} result ${resultIndex + 1} - ${time}`;
};

const getBodyText = (
  message: QueryMessage,
  debugInformation: QuerygenDebugInformation | null,
): string => {
  const parsed = parseJsonValue(message.text);
  if (isLegacyTablePayload(parsed) || isLegacyChartPayload(parsed) || isUnavailablePayload(parsed)) {
    return "";
  }

  const cleaned = stripInlineResultTable(message.text, message.metadata);
  if (!cleaned) {
    return "";
  }

  return cleaned;
};

const getFullAssistantContent = (
  message: QueryMessage,
  debugInformation: QuerygenDebugInformation | null,
  chartPayload: QuerygenLegacyChartPayload | null,
): string => {
  const dataArtifacts = getDataArtifactsFromMetadata(message.metadata);
  if (dataArtifacts.length > 1) {
    const resultText = dataArtifacts
      .map(
        (artifact, index) =>
          artifact.unavailableReason
            ? `Result ${index + 1}:\n${artifact.unavailableReason}`
            : `Result ${index + 1}:\n${JSON.stringify(artifact.rows, null, 2)}`,
      )
      .join("\n\n");

    return message.text ? `${message.text}\n\n${resultText}` : resultText;
  }

  if (debugInformation?.chartConfig && chartPayload) {
    const dataText = JSON.stringify(chartPayload.data, null, 2);
    return message.text ? `${message.text}\n\n${dataText}` : dataText;
  }

  const parsed = parseJsonValue(message.text);
  if (isUnavailablePayload(parsed)) {
    return parsed.reason;
  }

  return message.text;
};

const getAssistantCopyElement = (messageId: string): HTMLElement | null => {
  const elements = document.querySelectorAll<HTMLElement>("[data-assistant-copy-id]");
  return (
    Array.from(elements).find(
      (element) => element.dataset.assistantCopyId === messageId,
    ) ?? null
  );
};

const getRenderedClipboardHtml = (element: HTMLElement): string => {
  const clone = element.cloneNode(true) as HTMLElement;
  clone
    .querySelectorAll("[data-copy-exclude], button")
    .forEach((node) => node.remove());

  return clone.innerHTML.trim();
};

const writeAssistantClipboard = async (
  html: string | null,
  plainText: string,
): Promise<void> => {
  const normalizedPlainText = plainText.trim();
  const normalizedHtml = html?.trim();

  if (normalizedHtml && "ClipboardItem" in window && navigator.clipboard.write) {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([normalizedHtml], { type: "text/html" }),
        "text/plain": new Blob([normalizedPlainText], { type: "text/plain" }),
      }),
    ]);
    return;
  }

  await navigator.clipboard.writeText(normalizedPlainText);
};

type KnowledgeReference = {
  citationId: number;
  title: string;
  sourceName?: string;
  sourceUri?: string;
};

const parseCitationId = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : fallback;

const getReferenceHost = (sourceUri: string | undefined): string => {
  if (!sourceUri) {
    return "";
  }

  try {
    return new URL(sourceUri).hostname.replace(/^www\./, "");
  } catch {
    return sourceUri;
  }
};

const DATABASE_TOOL_NAMES = new Set([
  "list_tables",
  "describe_table",
  "sample_table",
  "execute_sql",
  "execute_final_query",
  "return_empty_final_result",
]);
const CITATION_MARKER_PATTERN = /\[\d+\]/;

const sidebarNavItems = [
  { icon: Home, label: "Home", href: APP_ROUTES.datascope },
  { icon: Pin, label: "Pinboards", href: APP_ROUTES.pinboards },
  { icon: Sparkles, label: "Agents", href: APP_ROUTES.agents },
  { icon: BookOpen, label: "Knowledge", href: APP_ROUTES.knowledgebase },
];

const sidebarMoreItems = [
  { icon: Search, label: "Gaps", href: APP_ROUTES.knowledgeGaps },
  { icon: Database, label: "Explore", href: APP_ROUTES.explore },
];

const getKnowledgeReferences = (
  metadata: QueryMessage["metadata"],
  assistantText = "",
): KnowledgeReference[] => {
  if (!isAssistantMetadata(metadata)) {
    return [];
  }

  const toolTrace = metadata.debug?.toolTrace ?? [];
  const usedDatabaseEvidence = toolTrace.some(
    (trace) => trace.event === "tool_output" && DATABASE_TOOL_NAMES.has(trace.toolName),
  );
  const hasInlineCitation = CITATION_MARKER_PATTERN.test(assistantText);
  if (
    (usedDatabaseEvidence || getDataArtifactsFromMetadata(metadata).length > 0) &&
    !hasInlineCitation
  ) {
    return [];
  }

  const references = new Map<string, KnowledgeReference>();

  for (const trace of toolTrace) {
    if (
      trace.event !== "tool_output" ||
      !["knowledgebase_prefetch", "search_knowledgebase"].includes(trace.toolName) ||
      !trace.output
    ) {
      continue;
    }

    const parsed = parseJsonValue(trace.output);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      continue;
    }

    const citations = (parsed as { citations?: unknown }).citations;
    if (!Array.isArray(citations)) {
      continue;
    }

    for (const [citationIndex, citation] of citations.entries()) {
      if (!citation || typeof citation !== "object" || Array.isArray(citation)) {
        continue;
      }

      const title =
        typeof citation.documentTitle === "string"
          ? citation.documentTitle.trim()
          : "";
      const sourceName =
        typeof citation.sourceName === "string" ? citation.sourceName.trim() : "";
      const sourceUri =
        typeof citation.sourceUri === "string"
          ? citation.sourceUri.trim()
          : "";

      if (!title) {
        continue;
      }

      const key = sourceUri || `${title}:${sourceName}`;
      const citationId = parseCitationId(
        (citation as { citationId?: unknown }).citationId,
        citationIndex + 1,
      );
      const existing = references.get(key);
      if (!existing) {
        references.set(key, {
          citationId,
          title,
          sourceName: sourceName || undefined,
          sourceUri: sourceUri || undefined,
        });
      } else if (citationId < existing.citationId) {
        references.set(key, {
          ...existing,
          citationId,
        });
      }
    }
  }

  return Array.from(references.values()).sort(
    (left, right) => left.citationId - right.citationId,
  );
};

const buildCsv = (rows: Array<Record<string, unknown>>): string => {
  if (!rows.length) {
    return "";
  }

  const headers = Array.from(
    rows.reduce<Set<string>>((keys, row) => {
      Object.keys(row).forEach((key) => keys.add(key));
      return keys;
    }, new Set<string>()),
  );

  const escapeCell = (value: unknown): string => {
    const text =
      value == null
        ? ""
        : typeof value === "object"
          ? JSON.stringify(value)
          : String(value);
    return `"${text.replaceAll('"', '""')}"`;
  };

  return [
    headers.join(","),
    ...rows.map((row) => headers.map((header) => escapeCell(row[header])).join(",")),
  ].join("\n");
};

const downloadCsv = (
  rows: Array<Record<string, unknown>>,
  filename: string,
): void => {
  const csv = buildCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const groupConversationBuckets = (
  conversations: QuerygenConversationSummary[],
): Array<{ title: string; items: QuerygenConversationSummary[] }> => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const buckets = {
    Today: [] as QuerygenConversationSummary[],
    Yesterday: [] as QuerygenConversationSummary[],
    "Previous 7 Days": [] as QuerygenConversationSummary[],
    "Previous 30 Days": [] as QuerygenConversationSummary[],
    Older: [] as QuerygenConversationSummary[],
  };

  conversations.forEach((conversation) => {
    const createdAt = new Date(conversation.createdAt);
    const onlyDate = new Date(
      createdAt.getFullYear(),
      createdAt.getMonth(),
      createdAt.getDate(),
    );

    if (onlyDate.getTime() === today.getTime()) {
      buckets.Today.push(conversation);
    } else if (onlyDate.getTime() === yesterday.getTime()) {
      buckets.Yesterday.push(conversation);
    } else if (createdAt >= sevenDaysAgo) {
      buckets["Previous 7 Days"].push(conversation);
    } else if (createdAt >= thirtyDaysAgo) {
      buckets["Previous 30 Days"].push(conversation);
    } else {
      buckets.Older.push(conversation);
    }
  });

  return Object.entries(buckets)
    .filter(([, items]) => items.length > 0)
    .map(([title, items]) => ({ title, items }));
};

const formatMessageTimestamp = (timestamp: number): string =>
  new Date(timestamp).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

type QuerygenPageProps = {
  pinboardsOnly?: boolean;
};

type PendingPinResult = {
  kind: PinboardPanelKind;
  rows: Array<Record<string, unknown>>;
  chartConfig?: QuerygenChartConfig | null;
  query?: string | null;
  title: string;
  resultKey: string;
};

export function QuerygenPage({
  pinboardsOnly = false,
}: QuerygenPageProps) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pinboardId: routePinboardId } = useParams<{ pinboardId?: string }>();
  const messageEndRef = useRef<HTMLDivElement | null>(null);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const sendLockRef = useRef(false);
  const streamingBufferRef = useRef("");
  const streamingFrameRef = useRef<number | null>(null);
  const autoRefreshPinboardRef = useRef<string | null>(null);
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [messages, setMessages] = useState<QueryMessage[]>([]);
  const [conversations, setConversations] = useState<QuerygenConversationSummary[]>(
    [],
  );
  const [draft, setDraft] = useState("");
  const [streamingText, setStreamingText] = useState("");
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isDeepThinking, setIsDeepThinking] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [debugMessageId, setDebugMessageId] = useState<string | null>(null);
  const [forceNewConversation, setForceNewConversation] = useState(false);
  const [deleteConversationId, setDeleteConversationId] = useState<string | null>(
    null,
  );
  const workspaceMode = pinboardsOnly ? "pinboards" : "chat";
  const [pinboards, setPinboards] = useState<Pinboard[]>([]);
  const [activePinboardId, setActivePinboardId] = useState<string | null>(null);
  const [panelResults, setPanelResults] = useState<
    Record<string, PinboardPanelResult>
  >({});
  const [pinnedResultKey, setPinnedResultKey] = useState<string | null>(null);
  const [isCreatePinboardDialogOpen, setIsCreatePinboardDialogOpen] =
    useState(false);
  const [newPinboardName, setNewPinboardName] = useState("");
  const [pendingPinResult, setPendingPinResult] = useState<PendingPinResult | null>(
    null,
  );
  const [savePinboardId, setSavePinboardId] = useState("");
  const [saveNewPinboardName, setSaveNewPinboardName] = useState("");
  const [savePanelTitle, setSavePanelTitle] = useState("");
  const [refreshingPanelIds, setRefreshingPanelIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const showPendingAssistant = sending || Boolean(streamingText);

  const cancelStreamingFrame = () => {
    if (streamingFrameRef.current !== null) {
      window.cancelAnimationFrame(streamingFrameRef.current);
      streamingFrameRef.current = null;
    }
    streamingBufferRef.current = "";
  };

  const clearStreamingResponse = () => {
    cancelStreamingFrame();
    setStreamingText("");
  };

  const appendStreamingResponse = (chunk: string) => {
    streamingBufferRef.current += chunk;
    if (streamingFrameRef.current !== null) {
      return;
    }

    streamingFrameRef.current = window.requestAnimationFrame(() => {
      streamingFrameRef.current = null;
      const nextText = streamingBufferRef.current;
      streamingBufferRef.current = "";
      if (nextText) {
        setStreamingText((current) => current + nextText);
      }
    });
  };

  useEffect(() => {
    const handlePointerDown = (event: globalThis.MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (accountMenuRef.current?.contains(target) || moreMenuRef.current?.contains(target)) {
        return;
      }

      setAccountMenuOpen(false);
      setMoreMenuOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => () => cancelStreamingFrame(), []);

  useEffect(() => {
    const syncCollapsed = () => {
      setSidebarCollapsed(window.innerWidth < 1100);
    };

    syncCollapsed();
    window.addEventListener("resize", syncCollapsed);
    return () => window.removeEventListener("resize", syncCollapsed);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!user?.activeOrganizationId) {
        setAgents([]);
        setSelectedAgentId("");
        setMessages([]);
        clearStreamingResponse();
        setConversationId(undefined);
        setConversations([]);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        setMessages([]);
        clearStreamingResponse();
        setConversationId(undefined);
        setError(null);
        const nextAgents = await apiRequest<AgentRecord[]>("/auth/agents");
        setAgents(nextAgents);
        if (nextAgents.length > 0) {
          setSelectedAgentId((current) =>
            nextAgents.some((agent) => agent.id === current) ? current : nextAgents[0]!.id,
          );
        } else {
          setSelectedAgentId("");
        }
      } catch (loadError) {
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Failed to load Datascope configuration",
        );
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [user?.activeOrganizationId]);

  useEffect(() => {
    if (agents.length === 0) {
      if (selectedAgentId) {
        setSelectedAgentId("");
      }
      return;
    }

    const currentSelection = agents.find((agent) => agent.id === selectedAgentId);
    if (currentSelection) {
      return;
    }

    if (agents[0] && agents[0].id !== selectedAgentId) {
      setSelectedAgentId(agents[0].id);
    }
  }, [agents, selectedAgentId]);

  useEffect(() => {
    const nextPinboards = readPinboards(user?.activeOrganizationId);
    const panelIds = nextPinboards.flatMap((pinboard) =>
      pinboard.panels.map((panel) => panel.id),
    );
    setPinboards(nextPinboards);
    setPanelResults(readPanelResults(panelIds, user?.activeOrganizationId));
    setActivePinboardId((current) => {
      if (routePinboardId && nextPinboards.some((pinboard) => pinboard.id === routePinboardId)) {
        return routePinboardId;
      }
      return current && nextPinboards.some((pinboard) => pinboard.id === current)
        ? current
        : nextPinboards[0]?.id ?? null;
    });
  }, [routePinboardId, user?.activeOrganizationId]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId) ?? null,
    [agents, selectedAgentId],
  );

  const conversationGroups = useMemo(
    () => groupConversationBuckets(conversations),
    [conversations],
  );

  const userDisplayName = makeUserName(user?.email);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const resetComposerState = () => {
    setMessages([]);
    clearStreamingResponse();
    setConversationId(undefined);
    setError(null);
    setForceNewConversation(false);
    setCopiedMessageId(null);
    setDebugMessageId(null);
  };

  const toQueryMessages = (
    payload: Awaited<ReturnType<typeof querygenApi.getConversationMessages>>,
  ): QueryMessage[] => {
    const wireMessages = Array.isArray(payload) ? payload : payload.messages;

    return wireMessages.map((message) => ({
      id: message.id,
      role: message.role === "assistant" ? "assistant" : "user",
      text: message.content.text,
      debugInformation: message.content.debug_information,
      timestamp: message.timestamp,
      conversationId: message.conversation_id,
      metadata: message.metadata ?? null,
    }));
  };

  const loadConversations = async (agentId?: string) => {
    if (!user?.activeOrganizationId || !agentId) {
      setConversations([]);
      return;
    }

    setLoadingHistory(true);
    try {
      const nextConversations = await querygenApi.listRecentConversations(
        agentId,
        50,
      );
      setConversations(nextConversations);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load conversations",
      );
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    void loadConversations(selectedAgent?.id);
  }, [selectedAgent?.id, user?.activeOrganizationId]);

  const handleAgentChange = (nextAgentId: string) => {
    if (!nextAgentId || nextAgentId === selectedAgentId) {
      return;
    }

    setSelectedAgentId(nextAgentId);
    resetComposerState();
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !user || sendLockRef.current) {
      return;
    }

    if (!selectedAgentId) {
      setError("No agent is configured for this workspace.");
      return;
    }

    const startsNewConversation = !conversationId || forceNewConversation;

    sendLockRef.current = true;
    setSending(true);
    clearStreamingResponse();
    setError(null);
    setMessages((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        role: "user",
        text,
        timestamp: Date.now(),
        conversationId,
      },
    ]);
    setDraft("");

    const decoder = new TextDecoder();
    let nextConversationId = conversationId;
    let buffered = "";
    let finalAssistantMessage: QueryMessage | null = null;
    const processStreamLine = (line: string) => {
      if (!line.startsWith("data: ")) {
        return;
      }

      let payload: Record<string, unknown>;
      try {
        payload = JSON.parse(line.slice(6));
      } catch {
        return;
      }

      if (typeof payload.error === "string") {
        throw new Error(payload.error);
      }

      if (typeof payload.content === "string") {
        appendStreamingResponse(payload.content);
      }

      if (typeof payload.conversationId === "string") {
        nextConversationId = payload.conversationId;
        setConversationId(payload.conversationId);
      }

      const msg = payload.message as {
        id: string;
        conversation_id: string;
        timestamp: number;
        content: { text: string; debug_information?: string };
        metadata?: QuerygenAssistantMetadata | null;
      } | undefined;
      if (msg?.id && msg?.content?.text !== undefined) {
        finalAssistantMessage = {
          id: msg.id,
          role: "assistant",
          text: msg.content.text,
          debugInformation: msg.content.debug_information,
          timestamp: msg.timestamp,
          conversationId: msg.conversation_id,
          metadata: msg.metadata ?? null,
        };
      }
    };

    try {
      const response = await querygenApi.sendMessage({
        agentId: selectedAgentId,
        message: text,
        conversationId,
        forceNewConversation: startsNewConversation,
        metadata: {
          source: "querygen",
          userName: makeUserName(user.email),
          deep_thinking: isDeepThinking ? "true" : "false",
        },
      });

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffered += decoder.decode(value, { stream: true });
        const lines = buffered.split("\n");
        buffered = lines.pop() || "";

        for (const line of lines) {
          processStreamLine(line);
        }
      }
      const remaining = `${buffered}${decoder.decode()}`.trim();
      if (remaining) {
        for (const line of remaining.split("\n")) {
          processStreamLine(line);
        }
      }

      if (finalAssistantMessage) {
        setMessages((current) => [...current, finalAssistantMessage as QueryMessage]);
      }

      if (nextConversationId) {
        try {
          const payload = await querygenApi.getConversationMessages(nextConversationId);
          setMessages(toQueryMessages(payload));
          setConversationId(nextConversationId);
        } catch {
          // Keep the optimistic transcript if the canonical refresh fails.
        }
      }

    } catch (sendError) {
      setError(
        sendError instanceof Error ? sendError.message : "Failed to send message",
      );
    } finally {
      sendLockRef.current = false;
      setSending(false);
      clearStreamingResponse();
      setForceNewConversation(false);
      if (selectedAgent?.id) {
        await loadConversations(selectedAgent.id);
      }
    }
  };

  const resetConversation = async () => {
    const previousConversationId = conversationId;

    setMessages([]);
    clearStreamingResponse();
    setConversationId(undefined);
    setForceNewConversation(true);
    setCopiedMessageId(null);
    setDebugMessageId(null);

    if (previousConversationId) {
      try {
        await querygenApi.setConversationStatus(previousConversationId, "closed");
      } catch (closeError) {
        setError(
          closeError instanceof Error
            ? closeError.message
            : "Failed to close conversation",
        );
      }
    }

    await loadConversations(selectedAgent?.id);
  };

  const handleConversationSelect = async (targetConversationId: string) => {
    setLoadingConversation(true);
    setError(null);

    try {
      const payload = await querygenApi.getConversationMessages(
        targetConversationId,
      );
      setMessages(toQueryMessages(payload));
      clearStreamingResponse();
      setConversationId(targetConversationId);
      setForceNewConversation(false);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load conversation",
      );
    } finally {
      setLoadingConversation(false);
    }
  };

  const handleConversationDelete = async (
    event: MouseEvent<HTMLButtonElement>,
    targetConversationId: string,
  ) => {
    event.stopPropagation();
    setDeleteConversationId(targetConversationId);
  };

  const handleConfirmConversationDelete = async () => {
    const targetConversationId = deleteConversationId;
    if (!targetConversationId) {
      return;
    }

    try {
      await querygenApi.deleteConversation(targetConversationId);
      if (conversationId === targetConversationId) {
        setMessages([]);
        clearStreamingResponse();
        setConversationId(undefined);
      }
      await loadConversations(selectedAgent?.id);
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Failed to delete conversation",
      );
    } finally {
      setDeleteConversationId(null);
    }
  };

  useEffect(() => {
    if (!sending && conversationId) {
      void loadConversations(selectedAgent?.id);
    }
  }, [sending, conversationId, selectedAgent?.id]);

  const activeDebugMessage = useMemo(
    () =>
      messages.find(
        (message) =>
          message.id === debugMessageId && isAssistantMetadata(message.metadata),
      ) ?? null,
    [debugMessageId, messages],
  );

  const handleCopyResponse = async (message: QueryMessage) => {
    try {
      const copyElement = getAssistantCopyElement(message.id);
      const fallbackText = getFullAssistantContent(
        message,
        extractDebugInformation(message.debugInformation, message.metadata),
        getChartPayload(
          message,
          extractDebugInformation(message.debugInformation, message.metadata),
        ),
      );
      await writeAssistantClipboard(
        copyElement ? getRenderedClipboardHtml(copyElement) : null,
        copyElement?.innerText || fallbackText,
      );
      setCopiedMessageId(message.id);
      window.setTimeout(() => {
        setCopiedMessageId((current) => (current === message.id ? null : current));
      }, 1600);
    } catch {
      setError("Failed to copy assistant response.");
    }
  };

  const persistPinboards = (nextPinboards: Pinboard[]) => {
    try {
      writePinboards(nextPinboards, user?.activeOrganizationId);
      setPinboards(nextPinboards);
    } catch {
      setError("Failed to save pinboard changes in this browser.");
    }
  };

  const setActivePinboard = (pinboardId: string) => {
    setActivePinboardId(pinboardId);
    if (pinboardsOnly) {
      navigate(`${APP_ROUTES.pinboards}/${pinboardId}`);
    }
  };

  const ensureActivePinboard = (): { pinboards: Pinboard[]; pinboardId: string } => {
    const existingPinboard =
      pinboards.find((pinboard) => pinboard.id === activePinboardId) ??
      pinboards[0] ??
      null;

    if (existingPinboard) {
      return { pinboards, pinboardId: existingPinboard.id };
    }

    const created = createPinboard("Main Pinboard");
    const nextPinboards = [created];
    setActivePinboardId(created.id);
    return { pinboards: nextPinboards, pinboardId: created.id };
  };

  const handleCreatePinboard = () => {
    setNewPinboardName("");
    setIsCreatePinboardDialogOpen(true);
  };

  const handleCreatePinboardSubmit = () => {
    const name = newPinboardName.trim();
    if (!name) {
      return;
    }

    const created = createPinboard(name);
    const nextPinboards = [...pinboards, created];
    setActivePinboardId(created.id);
    persistPinboards(nextPinboards);
    setIsCreatePinboardDialogOpen(false);
    setNewPinboardName("");
    if (pinboardsOnly) {
      navigate(`${APP_ROUTES.pinboards}/${created.id}`);
    }
  };

  const handlePinResult = (input: PendingPinResult) => {
    if (!input.rows.length) {
      setError("Cannot pin an empty result.");
      return;
    }

    if (!selectedAgentId || !input.query?.trim()) {
      setError("Cannot pin this result because its source query is unavailable.");
      return;
    }

    setPendingPinResult(input);
    setSavePinboardId(activePinboardId ?? pinboards[0]?.id ?? "new");
    setSaveNewPinboardName("");
    setSavePanelTitle(input.title);
  };

  const handleSavePinnedResult = () => {
    if (!pendingPinResult || !selectedAgentId || !pendingPinResult.query?.trim()) {
      return;
    }

    let targetPinboards = pinboards;
    let targetPinboardId = savePinboardId;

    if (savePinboardId === "new") {
      const name = saveNewPinboardName.trim();
      if (!name) {
        return;
      }
      const created = createPinboard(name);
      targetPinboards = [...pinboards, created];
      targetPinboardId = created.id;
    }

    if (!targetPinboardId) {
      const target = ensureActivePinboard();
      targetPinboards = target.pinboards;
      targetPinboardId = target.pinboardId;
    }

    const { pinboards: nextPinboards, panel } = addPanelToPinboard(
      targetPinboards,
      targetPinboardId,
      {
        title: savePanelTitle.trim() || pendingPinResult.title,
        kind: pendingPinResult.kind,
        agentId: selectedAgentId,
        query: pendingPinResult.query.trim(),
        chartConfig: pendingPinResult.chartConfig ?? null,
      },
    );

    setActivePinboardId(targetPinboardId);
    persistPinboards(nextPinboards);
    const initialResult: PinboardPanelResult = {
      query: pendingPinResult.query.trim(),
      rows: pendingPinResult.rows,
      rowCount: pendingPinResult.rows.length,
      truncated: false,
      refreshedAt: new Date().toISOString(),
      error: null,
    };
    try {
      writePanelResult(
        panel.id,
        initialResult,
        user?.activeOrganizationId,
      );
      setPanelResults((current) => ({ ...current, [panel.id]: initialResult }));
    } catch {
      setError("Failed to cache the pinned panel result in this browser.");
    }
    setPinnedResultKey(pendingPinResult.resultKey);
    const resultKey = pendingPinResult.resultKey;
    setPendingPinResult(null);
    window.setTimeout(() => {
      setPinnedResultKey((current) =>
        current === resultKey ? null : current,
      );
    }, 1600);
    navigate(`${APP_ROUTES.pinboards}/${targetPinboardId}`);
  };

  const handleDeletePanel = (pinboardId: string, panelId: string) => {
    const nextPinboards = removePanelFromPinboard(pinboards, pinboardId, panelId);
    persistPinboards(nextPinboards);
    removePanelResult(panelId, user?.activeOrganizationId);
    setPanelResults((current) => {
      const next = { ...current };
      delete next[panelId];
      return next;
    });
  };

  const handleRefreshPanel = async (pinboardId: string, panelId: string) => {
    const panel = pinboards
      .find((pinboard) => pinboard.id === pinboardId)
      ?.panels.find((candidate) => candidate.id === panelId);

    if (!panel) {
      return;
    }

    if (!panel.agentId || !panel.query) {
      const currentResult = panelResults[panelId];
      const errorResult: PinboardPanelResult = {
        rows: currentResult?.rows ?? [],
        rowCount: currentResult?.rowCount ?? 0,
        truncated: currentResult?.truncated ?? false,
        refreshedAt: new Date().toISOString(),
        error: "Panel is missing the agent or query needed to refresh.",
      };
      writePanelResult(panelId, errorResult, user?.activeOrganizationId);
      setPanelResults((current) => ({ ...current, [panelId]: errorResult }));
      return;
    }

    setRefreshingPanelIds((current) => new Set(current).add(panelId));
    try {
      const result = await querygenApi.executePanel({
        agentId: panel.agentId,
        query: panel.query,
      });
      const nextResult: PinboardPanelResult = {
        query: result.query,
        rows: result.rows,
        rowCount: result.rowCount,
        truncated: result.truncated,
        refreshedAt: new Date().toISOString(),
        error: null,
      };
      writePanelResult(panelId, nextResult, user?.activeOrganizationId);
      setPanelResults((current) => ({ ...current, [panelId]: nextResult }));
    } catch (refreshError) {
      const currentResult = panelResults[panelId];
      const errorResult: PinboardPanelResult = {
        rows: currentResult?.rows ?? [],
        rowCount: currentResult?.rowCount ?? 0,
        truncated: currentResult?.truncated ?? false,
        refreshedAt: new Date().toISOString(),
        error:
          refreshError instanceof Error
            ? refreshError.message
            : "Failed to refresh panel.",
      };
      writePanelResult(panelId, errorResult, user?.activeOrganizationId);
      setPanelResults((current) => ({ ...current, [panelId]: errorResult }));
    } finally {
      setRefreshingPanelIds((current) => {
        const next = new Set(current);
        next.delete(panelId);
        return next;
      });
    }
  };

  const handleRefreshPinboard = async (pinboardId: string) => {
    const pinboard = pinboards.find((candidate) => candidate.id === pinboardId);
    if (!pinboard) {
      return;
    }

    for (const panel of pinboard.panels) {
      await handleRefreshPanel(pinboardId, panel.id);
    }
  };

  useEffect(() => {
    if (!activePinboardId || workspaceMode !== "pinboards") {
      return;
    }

    const activePinboard = pinboards.find(
      (pinboard) => pinboard.id === activePinboardId,
    );
    if (!activePinboard?.panels.length) {
      return;
    }

    if (autoRefreshPinboardRef.current === activePinboardId) {
      return;
    }

    autoRefreshPinboardRef.current = activePinboardId;
    const timer = window.setTimeout(() => {
      void handleRefreshPinboard(activePinboardId);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [activePinboardId, pinboards, workspaceMode]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="rounded-full border border-[rgba(17,17,17,0.08)] bg-white px-5 py-3 text-sm text-[#616773] shadow-sm">
          Loading Datascope workspace...
        </div>
      </div>
    );
  }

  return (
    <div className="querygen-app min-h-screen text-[#111111]">
      <div className="querygen-shell relative flex h-dvh overflow-hidden">
        <aside
          className={`flex h-full shrink-0 flex-col overflow-hidden bg-[#f7f7f7] transition-all duration-200 ${
            sidebarCollapsed ? "w-[64px]" : "w-[260px]"
          }`}
        >
          <div className={sidebarCollapsed ? "p-3" : "px-4 pb-2 pt-4"}>
            {!sidebarCollapsed ? (
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => navigate(APP_ROUTES.datascope)}
                  className="flex min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[16px] font-semibold leading-none text-[#111111] transition-colors hover:bg-[#ececec]"
                >
                  <img src="/logo_small.svg" alt="" className="h-5 w-5 shrink-0" />
                  <span className="truncate">Datascope</span>
                </button>
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-[#6b6b6b] transition-colors hover:bg-[#ececec] hover:text-[#111111]"
                  title="Close sidebar"
                >
                  <PanelLeftClose className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={() => setSidebarCollapsed(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-[#6b6b6b] transition-colors hover:bg-[#ececec] hover:text-[#111111]"
                  title="Open sidebar"
                >
                  <PanelLeftOpen className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate(APP_ROUTES.datascope)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-[#ececec]"
                  title="Datascope"
                >
                  <img src="/logo_small.svg" alt="" className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>

          <div className={sidebarCollapsed ? "px-3" : "px-3 pb-2"}>
            {!sidebarCollapsed ? (
              <div className="space-y-1">
                <button
                  onClick={() => void resetConversation()}
                  className="flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium text-[#2f2f2f] transition-colors hover:bg-[#ececec]"
                >
                  <MessageSquarePlus className="h-[18px] w-[18px] text-[#202123]" />
                  <span>New chat</span>
                </button>
                {sidebarNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname.startsWith(item.href);

                  return (
                    <button
                      key={item.href}
                      type="button"
                      onClick={() => navigate(item.href)}
                      className={`flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium transition-colors ${
                        active
                          ? "bg-[#ececec] text-[#111111]"
                          : "text-[#2f2f2f] hover:bg-[#ececec]"
                      }`}
                    >
                      <Icon className="h-[18px] w-[18px] text-[#202123]" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
                <div ref={moreMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setAccountMenuOpen(false);
                      setMoreMenuOpen((current) => !current);
                    }}
                    className={`flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium transition-colors ${
                      sidebarMoreItems.some((item) =>
                        location.pathname.startsWith(item.href),
                      )
                        ? "bg-[#ececec] text-[#111111]"
                        : "text-[#2f2f2f] hover:bg-[#ececec]"
                    }`}
                  >
                    <Ellipsis className="h-[18px] w-[18px] text-[#202123]" />
                    <span>More</span>
                  </button>
                  {moreMenuOpen ? (
                    <div className="mt-1 rounded-2xl border border-[#e4e4e4] bg-white p-1.5 shadow-[0_14px_34px_rgba(0,0,0,0.1)]">
                      {sidebarMoreItems.map((item) => {
                        const Icon = item.icon;
                        const active = location.pathname.startsWith(item.href);

                        return (
                          <button
                            key={item.href}
                            type="button"
                            onClick={() => {
                              setMoreMenuOpen(false);
                              navigate(item.href);
                            }}
                            className={`flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium transition-colors ${
                              active
                                ? "bg-[#ececec] text-[#111111]"
                                : "text-[#2f2f2f] hover:bg-[#f3f3f3]"
                            }`}
                          >
                            <Icon className="h-[18px] w-[18px] text-[#202123]" />
                            <span>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={() => void resetConversation()}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-[#2f2f2f] transition-colors hover:bg-[#ececec]"
                  title="New chat"
                >
                  <MessageSquarePlus className="h-5 w-5" />
                </button>
                {sidebarNavItems.slice(1, 5).map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname.startsWith(item.href);

                  return (
                    <button
                      key={item.href}
                      type="button"
                      onClick={() => navigate(item.href)}
                      className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                        active
                          ? "bg-[#ececec] text-[#111111]"
                          : "text-[#2f2f2f] hover:bg-[#ececec]"
                      }`}
                      title={item.label}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {!sidebarCollapsed ? (
            <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto px-2 pb-4 pt-3">
              {pinboardsOnly ? (
                <div className="px-2">
                  <div className="px-2 pb-2 pt-1 text-[14px] font-semibold text-[#1f1f1f]">
                    Pinboards
                  </div>
                  <div className="space-y-1">
                    {pinboards.length === 0 ? (
                      <div className="px-2 py-8 text-[14px] leading-6 text-[#6b6b6b]">
                        No pinboards yet.
                      </div>
                    ) : (
                      pinboards.map((pinboard) => (
                        <button
                          key={pinboard.id}
                          type="button"
                          onClick={() => setActivePinboard(pinboard.id)}
                          className={`flex h-8 w-full items-center rounded-xl px-2 text-left text-[13px] leading-5 transition-colors ${
                            pinboard.id === activePinboardId
                              ? "bg-[#ececec] text-[#111111]"
                              : "text-[#2f2f2f] hover:bg-[#ececec]"
                          }`}
                        >
                          <span className="truncate">{pinboard.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : loadingHistory ? (
                <div className="space-y-2 px-2">
                  {[...Array(6)].map((_, index) => (
                    <div
                      key={index}
                      className="querygen-skeleton-line h-9 w-full rounded-xl"
                    />
                  ))}
                </div>
              ) : conversations.length === 0 ? (
                <div className="px-4 py-12">
                  <div className="text-[14px] font-medium text-[#1f1f1f]">
                    Recents
                  </div>
                  <p className="mt-3 text-[13px] leading-5 text-[#6b6b6b]">
                    Start chatting and threads will appear here automatically.
                  </p>
                </div>
              ) : (
                <div className="space-y-4 px-1">
                  {conversationGroups.map((group) => (
                    <div key={group.title}>
                      <div className="px-2 pb-1.5 text-[14px] font-semibold text-[#1f1f1f]">
                        {group.title === "Today" ? "Recents" : group.title}
                      </div>
                      <div className="space-y-1">
                        {group.items.map((conversation) => {
                          const isActive = conversation.id === conversationId;

                          return (
                            <div
                              key={conversation.id}
                              className={`group flex items-center gap-1 rounded-xl transition-colors ${
                                isActive
                                  ? "bg-[#ececec]"
                                  : "hover:bg-[#ececec]"
                              }`}
                            >
                              <button
                                onClick={() =>
                                  void handleConversationSelect(conversation.id)
                                }
                                className="min-w-0 flex-1 px-2 py-2 text-left"
                              >
                                <div className="line-clamp-1 text-[13px] font-normal leading-5 text-[#2f2f2f]">
                                  {conversation.title ||
                                    conversation.firstUserMessage ||
                                    "New conversation"}
                                </div>
                              </button>
                              <button
                                onClick={(event) =>
                                  void handleConversationDelete(
                                    event,
                                    conversation.id,
                                  )
                                }
                                className="mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[#8f8f8f] opacity-0 transition-all hover:bg-[#dedede] hover:text-[#dc2626] group-hover:opacity-100"
                                title="Delete conversation"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="min-h-0 flex-1" />
          )}

          {!sidebarCollapsed ? (
            <div ref={accountMenuRef} className="relative px-3 pb-3">
              {accountMenuOpen ? (
                <div className="absolute bottom-[68px] left-3 right-3 z-30 rounded-2xl border border-[#e4e4e4] bg-white p-1.5 shadow-[0_18px_42px_rgba(0,0,0,0.14)]">
                  <button
                    type="button"
                    onClick={() => {
                      setAccountMenuOpen(false);
                      navigate(APP_ROUTES.settings);
                    }}
                    className="flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium text-[#2f2f2f] transition-colors hover:bg-[#f3f3f3]"
                  >
                    <Settings className="h-[18px] w-[18px]" />
                    <span>Settings</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAccountMenuOpen(false);
                      navigate(APP_ROUTES.users);
                    }}
                    className="flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium text-[#2f2f2f] transition-colors hover:bg-[#f3f3f3]"
                  >
                    <Users className="h-[18px] w-[18px]" />
                    <span>Users</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleLogout()}
                    className="flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium text-[#2f2f2f] transition-colors hover:bg-[#f3f3f3]"
                  >
                    <LogOut className="h-[18px] w-[18px]" />
                    <span>Log out</span>
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setMoreMenuOpen(false);
                  setAccountMenuOpen((current) => !current);
                }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-[#2f2f2f] transition-colors hover:bg-[#ececec]"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#d9d9d9] text-[12px] font-semibold text-[#555555]">
                  {userDisplayName.slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-medium leading-5">
                    {userDisplayName}
                  </div>
                  <div className="truncate text-[12px] leading-4 text-[#777777]">
                    {user?.email ?? "Personal account"}
                  </div>
                </div>
              </button>
            </div>
          ) : null}
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-[72px] shrink-0 items-center justify-between gap-4 border-b border-[#f0f0f0] bg-white px-5 sm:px-8">
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="relative w-[260px] max-w-[min(320px,55vw)]">
                <select
                  value={selectedAgentId}
                  onChange={(event) => handleAgentChange(event.target.value)}
                  disabled={agents.length === 0 || sending}
                  className="h-10 w-full appearance-none rounded-xl border border-transparent bg-transparent px-3 pr-10 text-[18px] font-semibold text-[#2f2f2f] outline-none transition hover:border-[#e5e5e5] hover:bg-[#f7f7f7] focus:border-[#d6d6d6] focus:bg-[#f7f7f7] focus:ring-2 focus:ring-[#111111]/5 disabled:cursor-not-allowed disabled:text-[#9b9b9b]"
                >
                  {agents.length === 0 ? (
                    <option value="">No agent configured</option>
                  ) : (
                    agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))
                  )}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-2 flex w-7 items-center justify-center rounded-lg text-[#7a7a7a]">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 4.5L6 7.5L9 4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!pinboardsOnly ? (
                <button
                  onClick={() => setIsDeepThinking((current) => !current)}
                  className={`inline-flex h-10 items-center gap-2 rounded-xl px-3 text-[14px] font-medium transition-colors ${
                    isDeepThinking
                      ? "bg-[#111111] text-white"
                      : "text-[#5f5f5f] hover:bg-[#f4f4f4] hover:text-[#111111]"
                  }`}
                >
                  <Brain className="h-4 w-4" />
                  Deep Thinking
                </button>
              ) : null}
            </div>
          </header>

          <div className="relative min-h-0 flex-1">
            {error ? (
              <div className="absolute left-6 right-6 top-0 z-20 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:left-8 sm:right-8">
                {error}
              </div>
            ) : null}

            {workspaceMode === "pinboards" ? (
              <PinboardWorkspace
                pinboards={pinboards}
                activePinboardId={activePinboardId}
                onActivePinboardChange={setActivePinboard}
                onCreatePinboard={handleCreatePinboard}
                onDeletePanel={handleDeletePanel}
                onRefreshPanel={(pinboardId, panelId) =>
                  void handleRefreshPanel(pinboardId, panelId)
                }
                onRefreshPinboard={(pinboardId) => void handleRefreshPinboard(pinboardId)}
                refreshingPanelIds={refreshingPanelIds}
                panelResults={panelResults}
              />
            ) : !messages.length ? (
              <div className="flex h-full items-center justify-center px-6 sm:px-12">
                <div className="w-full max-w-[720px] -translate-y-[8vh]">
                  <div className="querygen-composer rounded-[28px] border border-[#d9d9d9] bg-white px-4 py-3">
                    <div className="flex items-start gap-2.5">
                      <textarea
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            void handleSend();
                          }
                        }}
                        placeholder="Ask anything"
                        className="min-h-[48px] flex-1 resize-none border-0 bg-transparent px-1 py-1 text-[16px] leading-7 text-[#111111] placeholder:text-[#8f8f8f] focus:outline-none"
                        rows={1}
                      />
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2.5">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-[12px] text-[#8f8f8f]">
                          {isDeepThinking ? "Deep thinking enabled" : "Enter to send"}
                        </span>
                      </div>
                      <button
                        onClick={() => void handleSend()}
                        disabled={!draft.trim() || sending}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-[#111111] text-white transition-all hover:bg-[#000000] disabled:bg-[#ececec] disabled:text-[#a3a3a3]"
                        title="Send"
                      >
                        <Send className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="thin-scrollbar h-full overflow-y-auto">
                  <div className="mx-auto max-w-[820px] px-6 pb-52 pt-10 sm:px-10 sm:pt-14">
                    <div className="flex min-w-0 flex-col">
                      <div>
                        {messages.map((message, index) => {
                          const previousMessage =
                            index > 0 ? messages[index - 1] : null;
                          const debugInformation = extractDebugInformation(
                            message.debugInformation,
                            message.metadata,
                          );
                          const dataArtifacts = getDataArtifactsFromMetadata(
                            message.metadata,
                          );
                          const structuredResults = dataArtifacts.length
                            ? dataArtifacts.map((artifact) => {
                                const chartConfig = artifact.chartConfig ?? null;
                                return {
                                  chartConfig,
                                  chartPayload: chartConfig
                                    ? toLegacyChartPayloadFromArtifact(artifact)
                                    : null,
                                  chartUnavailableNotice:
                                    artifact.chartUnavailableNotice ?? null,
                                  hasChartConfig: Boolean(chartConfig),
                                  title: artifact.title ?? null,
                                  description: artifact.description ?? null,
                                  unavailableReason:
                                    artifact.unavailableReason ?? null,
                                  query: artifact.query,
                                  rows: artifact.rows,
                                  tablePayload: toLegacyTablePayloadFromArtifact(artifact),
                                };
                              })
                            : (() => {
                                const tablePayload = getTablePayload(message);
                                const chartPayload = getChartPayload(
                                  message,
                                  debugInformation,
                                );
                                const chartConfig = debugInformation?.chartConfig ?? null;
                                const unavailableReason = getUnavailableReason(message);

                                return tablePayload || chartPayload || unavailableReason
                                  ? [
                                      {
                                        chartConfig,
                                        chartPayload,
                                        chartUnavailableNotice: null,
                                        hasChartConfig: Boolean(chartConfig),
                                        title: null,
                                        description: null,
                                        unavailableReason,
                                        query: debugInformation?.query ?? null,
                                        rows:
                                          chartPayload?.data ??
                                          (tablePayload
                                            ? tablePayloadToObjects(tablePayload)
                                            : []),
                                        tablePayload,
                                      },
                                    ]
                                  : [];
                              })();
                          const bodyText = getBodyText(message, debugInformation);
                          const knowledgeReferences = getKnowledgeReferences(
                            message.metadata,
                            bodyText,
                          );
                          const hasStructuredResults = structuredResults.length > 0;
                          const spacingClass =
                            index === 0
                              ? ""
                              : message.role === "user" &&
                                  previousMessage?.role === "user"
                                ? "mt-4"
                                : message.role === "user"
                                  ? "mt-7"
                                  : previousMessage?.role === "user"
                                    ? "mt-8"
                                    : "mt-10";

                          return (
                            <div
                              key={message.id}
                              className={`querygen-fade-up ${spacingClass}`}
                            >
                              {message.role === "user" ? (
                                <div className="ml-auto max-w-[min(34rem,100%)] rounded-3xl bg-[#f4f4f4] px-4 py-2.5 text-[15px] leading-7 text-[#111111]">
                                  {message.text}
                                </div>
                              ) : (
                                <div className="min-w-0 max-w-[760px] space-y-3">
                                  <div className="flex flex-wrap items-center gap-3">
                                    <div className="text-[12px] text-[#8f8f8f]">
                                      {formatMessageTimestamp(message.timestamp)}
                                    </div>
                                  </div>
                                  <div data-assistant-copy-id={message.id} className="space-y-3">
                                    {bodyText ? (
                                      <div className="querygen-markdown prose prose-sm max-w-none prose-headings:mb-4 prose-headings:mt-8 prose-p:my-0 prose-pre:my-5 prose-ul:my-4">
                                        <MDRenderer content={bodyText} />
                                      </div>
                                    ) : null}
                                    {structuredResults.map((result, resultIndex) => (
                                      <div
                                        key={`${message.id}-result-${resultIndex}`}
                                        className={resultIndex > 0 ? "pt-6" : ""}
                                      >
                                      {result.tablePayload ||
                                      (result.hasChartConfig &&
                                        result.chartPayload &&
                                        result.chartConfig) ? (
                                        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                          <div className="min-w-0">
                                            <div className="querygen-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                                              {structuredResults.length > 1
                                                ? `Result ${resultIndex + 1}`
                                                : "Result"}
                                            </div>
                                            {result.title ? (
                                              <div className="mt-1 text-[14px] font-semibold leading-6 text-[#111111]">
                                                {result.title}
                                              </div>
                                            ) : null}
                                            {result.description ? (
                                              <div className="mt-0.5 max-w-[560px] text-[12px] leading-5 text-[#5f6470]">
                                                {result.description}
                                              </div>
                                            ) : null}
                                          </div>
                                          <div className="flex flex-wrap items-center gap-2" data-copy-exclude>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handlePinResult({
                                                  kind:
                                                    result.hasChartConfig &&
                                                    result.chartConfig
                                                      ? "chart"
                                                      : "table",
                                                  rows: result.rows,
                                                  chartConfig: result.chartConfig,
                                                  query: result.query,
                                                  resultKey: `${message.id}-${resultIndex}`,
                                                  title: makePinboardPanelTitle(
                                                    result.hasChartConfig &&
                                                      result.chartConfig
                                                      ? "chart"
                                                      : "table",
                                                    result.chartConfig?.title ??
                                                      result.title ??
                                                      undefined,
                                                    message.timestamp,
                                                    resultIndex,
                                                  ),
                                                })
                                              }
                                              className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(17,17,17,0.08)] bg-white px-3 py-1.5 text-[12px] font-medium text-[#5f6470] transition-colors hover:text-[#111111]"
                                            >
                                              {pinnedResultKey ===
                                              `${message.id}-${resultIndex}` ? (
                                                <Check className="h-3.5 w-3.5" />
                                              ) : (
                                                <Pin className="h-3.5 w-3.5" />
                                              )}
                                              {pinnedResultKey ===
                                              `${message.id}-${resultIndex}`
                                                ? "Pinned"
                                                : "Pin"}
                                            </button>
                                            {!result.hasChartConfig && result.tablePayload ? (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                downloadCsv(
                                                  tablePayloadToObjects(result.tablePayload),
                                                  `querygen-${new Date(message.timestamp)
                                                    .toISOString()
                                                    .slice(0, 19)
                                                    .replaceAll(":", "-")}-result-${resultIndex + 1}.csv`,
                                                )
                                              }
                                              className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(17,17,17,0.08)] bg-white px-3 py-1.5 text-[12px] font-medium text-[#5f6470] transition-colors hover:text-[#111111]"
                                            >
                                              <Download className="h-3.5 w-3.5" />
                                              CSV
                                            </button>
                                            ) : null}
                                          </div>
                                        </div>
                                      ) : null}
                                      {result.chartUnavailableNotice ? (
                                        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-6 text-amber-900">
                                          {result.chartUnavailableNotice}
                                        </div>
                                      ) : null}
                                      {result.unavailableReason ? (
                                        <div className="rounded-md border border-slate-200/80 bg-slate-50 px-5 py-6 text-[13px] leading-6 text-slate-700">
                                          {result.unavailableReason}
                                        </div>
                                      ) : null}
                                      {!result.hasChartConfig && result.tablePayload ? (
                                        <QueryResultTable payload={result.tablePayload} />
                                      ) : null}
                                      {result.hasChartConfig &&
                                      result.chartPayload &&
                                      result.chartConfig ? (
                                        <div className="pt-2">
                                          <QueryResultChart
                                            data={result.chartPayload.data}
                                            chartConfig={result.chartConfig}
                                            downloadFilename={`querygen-${new Date(message.timestamp)
                                              .toISOString()
                                              .slice(0, 19)
                                              .replaceAll(":", "-")}-result-${resultIndex + 1}.png`}
                                          />
                                        </div>
                                      ) : null}
                                      </div>
                                    ))}
                                    {knowledgeReferences.length ? (
                                      <div className="overflow-hidden rounded-lg border border-[#d8e1ea] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                                        <div className="flex items-center justify-between gap-3 border-b border-[#edf1f5] px-4 py-2.5">
                                          <div className="flex min-w-0 items-center gap-2 text-[12px] font-semibold text-[#1f2937]">
                                            <BookOpen className="h-3.5 w-3.5 text-[#64748b]" />
                                            <span>Sources</span>
                                          </div>
                                          <div className="querygen-mono text-[10px] uppercase tracking-[0.14em] text-[#94a3b8]">
                                            {knowledgeReferences.length} cited
                                          </div>
                                        </div>
                                        <div className="divide-y divide-[#edf1f5]">
                                          {knowledgeReferences.map((reference) => {
                                            const host = getReferenceHost(
                                              reference.sourceUri,
                                            );
                                            const secondaryLabel =
                                              reference.sourceName &&
                                              reference.sourceName !== reference.title
                                                ? reference.sourceName
                                                : host;
                                            const content = (
                                              <>
                                                <span className="flex h-6 w-8 shrink-0 items-center justify-center rounded-md border border-[#d8e1ea] bg-[#f8fafc] text-[11px] font-semibold text-[#475569]">
                                                  [{reference.citationId}]
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                  <span className="block truncate text-[13px] font-medium leading-5 text-[#111827]">
                                                    {reference.title}
                                                  </span>
                                                  {secondaryLabel ? (
                                                    <span className="block truncate text-[11px] leading-4 text-[#64748b]">
                                                      {secondaryLabel}
                                                    </span>
                                                  ) : null}
                                                </span>
                                                {reference.sourceUri ? (
                                                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[#94a3b8]" />
                                                ) : null}
                                              </>
                                            );

                                            return reference.sourceUri ? (
                                              <a
                                                key={reference.sourceUri}
                                                href={reference.sourceUri}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex min-w-0 items-center gap-3 px-4 py-3 transition-colors hover:bg-[#f8fafc]"
                                              >
                                                {content}
                                              </a>
                                            ) : (
                                              <div
                                                key={`${reference.citationId}-${reference.title}`}
                                                className="flex min-w-0 items-center gap-3 px-4 py-3"
                                              >
                                                {content}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-4 pt-1">
                                    <button
                                      onClick={() => void handleCopyResponse(message)}
                                      className="inline-flex h-auto items-center gap-1.5 rounded-none px-0 py-0 text-[13px] font-medium text-[#5f6470] transition-colors hover:text-[#111111]"
                                    >
                                      {copiedMessageId === message.id ? (
                                        <Check className="h-3.5 w-3.5" />
                                      ) : (
                                        <Copy className="h-3.5 w-3.5" />
                                      )}
                                      {copiedMessageId === message.id ? "Copied" : "Copy"}
                                    </button>

                                    {isAssistantMetadata(message.metadata) ? (
                                      <button
                                        onClick={() => setDebugMessageId(message.id)}
                                        className="inline-flex h-auto items-center gap-1.5 rounded-none px-0 py-0 text-[13px] font-medium text-[#5f6470] transition-colors hover:text-[#111111]"
                                      >
                                        <Bug className="h-3.5 w-3.5" />
                                        Debug
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {showPendingAssistant ? (
                          <div className="querygen-fade-up">
                            {!streamingText && sending ? (
                              <div className="max-w-[720px]">
                                <div className="querygen-thinking-pill">
                                  <span className="querygen-live-dot" />
                                  <span>Thinking</span>
                                </div>
                              </div>
                            ) : null}
                            {streamingText ? (
                              <div className="querygen-streaming-response max-w-[720px]" aria-live="polite">
                                <div className="querygen-streaming-status">
                                  <span className="querygen-live-dot" />
                                  <span>Responding</span>
                                </div>
                                <div className="querygen-markdown prose prose-sm max-w-none text-[15px] leading-8 prose-headings:mb-4 prose-headings:mt-8 prose-p:my-0 prose-pre:my-5 prose-ul:my-4">
                                  <MDRenderer content={streamingText} />
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        <div ref={messageEndRef} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pointer-events-none absolute bottom-0 left-0 right-0 px-6 pb-6 pt-20 sm:px-10 sm:pb-8">
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-[#ffffff] via-[#ffffff]/96 to-transparent" />
                  <div className="pointer-events-auto mx-auto max-w-[720px]">
                    <div className="querygen-composer rounded-[28px] border border-[#d9d9d9] bg-white px-4 py-3">
                      <div className="flex items-start gap-2.5">
                        <textarea
                          value={draft}
                          onChange={(event) => setDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                              event.preventDefault();
                              void handleSend();
                            }
                          }}
                          placeholder="Ask anything"
                          className="min-h-[48px] flex-1 resize-none border-0 bg-transparent px-1 py-1 text-[16px] leading-7 text-[#111111] placeholder:text-[#8f8f8f] focus:outline-none"
                          rows={1}
                        />
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2.5">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span className="truncate text-[12px] text-[#8f8f8f]">
                            {isDeepThinking ? "Deep thinking enabled" : "Enter to send"}
                          </span>
                        </div>
                        <button
                          onClick={() => void handleSend()}
                          disabled={!draft.trim() || sending}
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-[#111111] text-white transition-all hover:bg-[#000000] disabled:bg-[#ececec] disabled:text-[#a3a3a3]"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {loadingConversation ? (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-[2px]">
                <div className="flex items-center gap-3 text-[#5f6470]">
                  <span className="querygen-thinking-text text-[15px] font-medium tracking-[-0.02em]">
                    Thinking
                  </span>
                  <span className="querygen-mono flex items-center gap-1 text-[11px] uppercase tracking-[0.16em]">
                    <Clock3 className="h-3 w-3" />
                    Restoring conversation
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <Dialog
        open={isCreatePinboardDialogOpen}
        onOpenChange={setIsCreatePinboardDialogOpen}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Create Pinboard</DialogTitle>
            <DialogDescription>
              Create a live dashboard for pinned query result panels.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="pinboard-name">Name</Label>
            <Input
              id="pinboard-name"
              value={newPinboardName}
              onChange={(event) => setNewPinboardName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleCreatePinboardSubmit();
                }
              }}
              placeholder="Revenue dashboard"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCreatePinboardDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCreatePinboardSubmit}
              disabled={!newPinboardName.trim()}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(deleteConversationId)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConversationId(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete Conversation</DialogTitle>
            <DialogDescription>
              This removes the conversation history and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteConversationId(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleConfirmConversationDelete()}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(pendingPinResult)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingPinResult(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Save Query to Pinboard</DialogTitle>
            <DialogDescription>
              Save this query as a live panel that can refresh from its source.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-5">
            <div className="space-y-2">
              <Label>Pinboard</Label>
              <Select value={savePinboardId} onValueChange={setSavePinboardId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a pinboard" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Create new pinboard</SelectItem>
                  {pinboards.map((pinboard) => (
                    <SelectItem key={pinboard.id} value={pinboard.id}>
                      {pinboard.name} ({pinboard.panels.length} panels)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {savePinboardId === "new" ? (
                <Input
                  value={saveNewPinboardName}
                  onChange={(event) => setSaveNewPinboardName(event.target.value)}
                  placeholder="New pinboard name"
                />
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="panel-title">Panel title</Label>
              <Input
                id="panel-title"
                value={savePanelTitle}
                onChange={(event) => setSavePanelTitle(event.target.value)}
                placeholder="Panel title"
              />
            </div>
            <div className="space-y-2">
              <Label>Query</Label>
              <div className="max-h-36 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3">
                <code className="whitespace-pre-wrap text-[12px] leading-5 text-slate-700">
                  {pendingPinResult?.query}
                </code>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingPinResult(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSavePinnedResult}
              disabled={
                !savePanelTitle.trim() ||
                !savePinboardId ||
                (savePinboardId === "new" && !saveNewPinboardName.trim())
              }
            >
              Pin It
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <QuerygenDebugDialog
        open={Boolean(activeDebugMessage)}
        onClose={() => setDebugMessageId(null)}
        metadata={
          activeDebugMessage && isAssistantMetadata(activeDebugMessage.metadata)
            ? activeDebugMessage.metadata
            : null
        }
      />
    </div>
  );
}
